// AuthContext shim — match interface useAuth() của Core (attendance-app), backed bởi zustand authStore của SaaS.
// Components copy từ Core dùng `useAuth()` sẽ chạy được mà KHÔNG cần đổi.
//
// Core's useAuth() trả: { user, loading, login, logout, loginWithGoogle, setPreference, ... }
// Mình map các field dùng nhiều + setPreference (Core MobileSection cần — TypeError nếu thiếu).

import { createContext, useContext, useEffect, useState } from 'react';
import { useAuthStore, mergeSessionUser, isSameWorkspace } from '../lib/authStore';
import { inboxClient, authApi, mfaApi, resetAuthBroken, baseURL } from '../lib/inboxClient';
import { readDeviceTrust, rememberDeviceTrust } from '../lib/deviceTrust';
import { unsubscribeWebPush } from '../lib/webPushLogout';
import { applyServerLocale, getLocale } from '@cnv/ui/i18n';

const AuthCtx = createContext(null);

// Đồng bộ ngôn ngữ 2 chiều quanh login:
//  • server → client: user.locale từ hồ sơ áp vào UI, NHƯNG chỉ khi user chưa tự chọn
//    trên máy này (applyServerLocale tôn trọng localStorage — lựa chọn tại chỗ thắng).
//  • client → server: user đã chọn ở màn login (trước khi có phiên) mà hồ sơ đang khác
//    → PATCH về meta.users.locale để thiết bị khác + BE (mail/push) theo đúng.
function syncLocaleAfterLogin(user) {
  try {
    const chosen = getLocale();
    applyServerLocale(user?.locale);
    if (user?.locale && chosen !== user.locale && getLocale() === chosen) {
      inboxClient.patch('/auth/me/locale', { locale: chosen }).catch(() => {});
    }
  } catch { /* i18n sync là best-effort, không được chặn login */ }
}

// Debounced flush — gộp double-update vào 1 PUT/key. _timers + _pending shared module-level.
const _timers = {};
const _pending = {};

function setPreference(key, value) {
  // Optimistic update store: user.preferences[key] = value
  const store = useAuthStore.getState();
  const next = {
    ...(store.user || {}),
    preferences: { ...(store.user?.preferences || {}), [key]: value },
  };
  useAuthStore.setState({ user: next });

  _pending[key] = value;
  if (_timers[key]) clearTimeout(_timers[key]);
  _timers[key] = setTimeout(() => {
    const v = _pending[key];
    delete _pending[key];
    inboxClient.put('/auth/me/preferences', { [key]: v }).catch(() => {});
  }, 300);
}

// Flush pending preferences trước khi tab close — keepalive để complete sau unload.
if (typeof window !== 'undefined') {
  const flush = () => {
    const keys = Object.keys(_pending);
    if (!keys.length) return;
    try {
      const token = useAuthStore.getState().accessToken;
      const body = JSON.stringify({ ..._pending });
      // baseURL tuyệt đối: hub.cnvwork.com không proxy /api nên '/api/v1/...' tương đối chỉ chạm
      // nginx static của hub (404/405), preferences không bao giờ được lưu.
      fetch(`${baseURL}/auth/me/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body, keepalive: true,
      }).catch(() => {});
      for (const k of keys) delete _pending[k];
    } catch { /* ignore */ }
  };
  window.addEventListener('pagehide', flush);
  window.addEventListener('beforeunload', flush);
}

// Lỗi 403 «cần thêm một bước» của đăng nhập (xác thực 2 lớp / đặt mật khẩu mới) — xem cnv-ui LoginChallenge.
function challengeOf(err, email = null) {
  const d = err?.response?.data;
  const details = d?.error?.details;
  if (!details?.challenge_token) return null;
  return { code: d.error.code, message: d.message || d.error.message || '', details, email };
}

export function AuthProvider({ children }) {
  const { user, accessToken, activeTenant, logout: doLogout } = useAuthStore();
  // Bước hai đang chờ của đăng nhập. State React (không persist): F5 giữa chừng = đăng nhập lại từ đầu — mã thử thách
  // chỉ sống 5–10 phút, giữ qua F5 chỉ đẻ trạng thái treo.
  const [challenge, setChallenge] = useState(null);

  // Phản hồi đăng nhập ĐẦY ĐỦ (mọi đường: mật khẩu, Google, bước hai) ⇒ lưu phiên + thẻ thiết bị tin cậy.
  const finishLogin = (result) => {
    useAuthStore.getState().setSession(result);
    rememberDeviceTrust(result);
    resetAuthBroken();                            // Chunk B: re-enable inboxClient requests sau login lại
    syncLocaleAfterLogin(result.user);
    setChallenge(null);
    return result.user;
  };
  // Chạy một bước; máy chủ đòi thêm bước ⇒ giữ bước đó làm `pending` rồi ném lại cho trang (trang không toast).
  const step = async (fn, email = null) => {
    try { return await fn(); } catch (err) {
      const next = challengeOf(err, email ?? challenge?.email ?? null);
      if (next) setChallenge(next);
      throw err;
    }
  };
  const challengeToken = () => challenge?.details?.challenge_token;
  const trustFor = () => readDeviceTrust(challenge?.email);

  // Auto-refresh /auth/me KHI app mount (1 LẦN DUY NHẤT). Đảm bảo store cập
  // nhật role + private_modules mới nhất từ BE mà không cần user logout/login.
  //
  // QUAN TRỌNG (2026-05-27 fix sau bug burst 429):
  // - deps `[]` chứ KHÔNG `[accessToken]` — accessToken là string string, identity
  //   ổn định, nhưng nếu zustand persist re-hydrate gây 2 lần render thì cũng chỉ
  //   trigger 1 lần useEffect khi mount + sau khi state hydrate.
  // - SHALLOW COMPARE trước setState — chỉ update store NẾU data THẬT SỰ khác.
  //   Tránh tạo new object identity → trigger re-render → child useEffect([user])
  //   re-run → burst fetch (WebPushRegister, CnvChatIntegrationPage, etc.).
  useEffect(() => {
    const token = useAuthStore.getState().accessToken;
    if (!token) return;
    inboxClient.get('/auth/me').then((r) => {
      const fresh = r.data?.user;
      const freshTenant = r.data?.active_tenant;
      if (!fresh) return;
      // i18n: hồ sơ đổi locale từ thiết bị khác → tab này pickup khi mount.
      // applyServerLocale KHÔNG đè lựa chọn user đã tự chọn trên máy này.
      try { applyServerLocale(fresh.locale); } catch { /* best-effort */ }
      const cur = useAuthStore.getState().user;
      const curTenant = useAuthStore.getState().activeTenant || {};
      // Gộp qua mergeSessionUser — MỌI trường /auth/me trả đều được so, trả lại đúng object cũ nếu
      // không có gì đổi (giữ identity, tránh child re-render). Bản trước tự liệt kê 7 trường để so và
      // sót `module_seats_enforced`: bật chặn chỗ ngồi xong, người đang mở phiên không bao giờ thấy khoá.
      const nextUser = mergeSessionUser(cur, fresh, { sameWorkspace: isSameWorkspace(curTenant, freshTenant) });
      const userChanged = nextUser !== cur;
      const tenantChanged = freshTenant && (
        curTenant.tenant_id !== freshTenant.tenant_id ||
        JSON.stringify(curTenant.private_modules || {}) !== JSON.stringify(freshTenant.private_modules || {}) ||
        JSON.stringify(curTenant.branding || null) !== JSON.stringify(freshTenant.branding || null)
      );
      if (!userChanged && !tenantChanged) return;        // No-op
      useAuthStore.setState({
        user: nextUser,
        activeTenant: tenantChanged ? { ...curTenant, ...freshTenant } : curTenant,
      });
    }).catch(() => { /* silent — auth interceptor sẽ handle 401 */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Run once on mount

  // Map về shape Core's useAuth — Core không có activeTenant nhưng có thể bổ sung
  const value = {
    user,
    accessToken,                                  // expose để useChat (cnv-ui) dùng cho socket auth
    loading: false,
    isAuthenticated: !!accessToken,
    activeTenant,                                 // bonus cho SaaS
    // Đăng xuất phải REVOKE server-side: store.logout() chỉ xoá localStorage nên refresh token
    // vẫn sống tới 7 ngày (giờ là sliding → vô hạn) và vẫn hiện ở màn "Thiết bị đang đăng nhập".
    // best-effort: mạng lỗi thì vẫn xoá phiên local, không giữ user lại trong app.
    logout: async () => {
      const refreshToken = useAuthStore.getState().refreshToken;
      // Trình duyệt này thôi nhận Web Push (sự cố 21/10/2026 — đã thoát vẫn hiện tin khách).
      const pushOff = unsubscribeWebPush();
      if (refreshToken) {
        // Thao tác THOÁT không được phụ thuộc vào mạng. `_retryCount: 3` để request này bỏ qua
        // lưới auto-retry transient (3×3s) của interceptor, timeout ngắn, và chờ tối đa 3s rồi
        // vẫn xoá phiên local. '/auth/logout' cũng nằm trong AUTH_BROKEN_ALLOWLIST nên không
        // kích proactive refresh (ladder ~70s).
        const revoke = inboxClient
          .post('/auth/logout', { refreshToken }, { timeout: 5000, _retryCount: 3 })
          .catch(() => {});
        await Promise.race([Promise.all([revoke, pushOff]), new Promise((r) => { setTimeout(r, 3000); })]);
      } else {
        await Promise.race([pushOff, new Promise((r) => { setTimeout(r, 3000); })]);
      }
      doLogout();
    },
    setPreference,                                // Core MobileSection + Tasks/CRM filters dùng
    // Login methods — match HOST_CONTRACT.md. Dùng bởi @cnv/ui/pages/auth/LoginPage.
    login: async (email, password) => {
      const result = await step(() => authApi.login(email, password), email);
      return finishLogin(result);
    },
    loginWithGoogle: async (arg) => {
      // arg: idToken (string) hoặc { code } (auth-code flow — kèm calendar scope).
      const result = await step(() => authApi.loginWithGoogle(arg));
      return finishLogin(result);
    },
    // Bước hai của đăng nhập — cnv-ui LoginPage / trang nhận lời mời dựng LoginChallenge từ khối này (HOST_CONTRACT.md).
    loginChallenge: {
      pending: challenge,
      begin: (err) => { const c = challengeOf(err); if (c) setChallenge(c); return !!c; },
      cancel: () => setChallenge(null),
      verifyMfa: (body) => step(() => authApi.loginMfa({
        challenge_token: challengeToken(), ...body,
        ...(trustFor() ? { device_trust: trustFor() } : {}),
      })),
      startEnroll: () => step(() => authApi.loginMfaEnrollStart(challengeToken())),
      confirmEnroll: (code) => step(() => authApi.loginMfaEnrollConfirm(challengeToken(), code, trustFor())),
      completePasswordChange: (newPassword) => step(
        () => authApi.passwordChangeRequired(challengeToken(), newPassword, trustFor()),
      ),
      // Gọi SAU khi người dùng đã lưu mã khôi phục (lưu phiên là trang tự chuyển đi, mất màn mã khôi phục).
      complete: async (result) => finishLogin(result),
    },
    // Xác thực 2 lớp của chính mình + chính sách workspace (cnv-ui MfaSettingsCard / WorkspaceMfaPolicyCard).
    mfa: mfaApi,
    // Quên mật khẩu — dùng bởi @cnv/ui/pages/auth/{ForgotPasswordPage,ResetPasswordPage}.
    // Xem HOST_CONTRACT.md. Đặt lại xong BE đã revoke hết session nên KHÔNG tự đăng nhập
    // ở đây: người dùng quay về /login gõ mật khẩu mới (đúng kỳ vọng + tránh phiên lửng lơ).
    forgotPassword: (email) => authApi.forgotPassword(email),
    verifyResetToken: (token) => authApi.verifyResetToken(token),
    resetPassword: (token, newPassword) => authApi.resetPassword(token, newPassword),
    // Đổi ngôn ngữ TRONG app (menu user): kernel đã đổi UI + localStorage; đây chỉ
    // đồng bộ về hồ sơ để thiết bị khác / BE compose text đúng ngôn ngữ.
    changeLocale: async (code) => {
      await inboxClient.patch('/auth/me/locale', { locale: code });
    },
    listMyTenants: async () => {
      try {
        const tenants = await authApi.listMyTenants();
        return (tenants || []).filter((t) => t.status !== 'terminated');
      } catch {
        return [];
      }
    },
    // Re-fetch /auth/me → cập nhật user + activeTenant trong zustand store.
    // Khi tenant flag thay đổi (vd private_modules.team_chat=true) hoặc role
    // update, FE sẽ pickup mà KHÔNG cần logout/login.
    refreshUser: async () => {
      try {
        const r = await inboxClient.get('/auth/me');
        const fresh = r.data?.user;
        const freshTenant = r.data?.active_tenant;
        if (fresh) {
          const curTenant = useAuthStore.getState().activeTenant || {};
          useAuthStore.setState({
            user: mergeSessionUser(useAuthStore.getState().user, fresh, {
              sameWorkspace: isSameWorkspace(curTenant, freshTenant),
            }),
            // Merge tenant — preserve fields BE không trả (vd nếu /auth/me thiếu)
            activeTenant: freshTenant ? { ...curTenant, ...freshTenant } : curTenant,
          });
          return fresh;
        }
      } catch { /* */ }
      return null;
    },
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

/** Hook tương thích với Core's useAuth(). */
export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    // Fallback — direct dùng store nếu Provider chưa wrap (ví dụ test isolated component)
    const { user, accessToken, activeTenant, logout } = useAuthStore.getState();
    return {
      user, accessToken, loading: false, isAuthenticated: !!accessToken, activeTenant, logout,
      setPreference,
      login: async () => null, loginWithGoogle: async () => null, refreshUser: async () => null,
    };
  }
  return ctx;
}
